import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Star, MessageCircle, Send, TrendingUp, Camera, Filter,
  CheckCircle, Trash2, X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RestaurantReviews() {
  useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total: 0, avg_rating: 0, distribution: {}, without_response: 0, photos_count: 0 });
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortFilter, setSortFilter] = useState('recent');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/restaurant-dashboard/reviews`);
      setReviews(response.data.reviews || []);
      setStats(response.data.stats || { total: 0, avg_rating: 0 });
    } catch {
      toast.error('Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmitResponse = async (reviewId) => {
    if (!responseText.trim()) {
      toast.error('Veuillez écrire une réponse');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/restaurant-dashboard/reviews/${reviewId}/respond`, {
        response: responseText
      });
      
      toast.success('Réponse envoyée !');
      setRespondingTo(null);
      setResponseText('');
      fetchReviews();
    } catch {
      toast.error('Erreur lors de l\'envoi de la réponse');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter and sort reviews
  const filteredReviews = reviews
    .filter(r => ratingFilter === 'all' || r.rating === parseInt(ratingFilter))
    .sort((a, b) => {
      switch (sortFilter) {
        case 'rating_high':
          return b.rating - a.rating;
        case 'rating_low':
          return a.rating - b.rating;
        case 'without_response':
          return (a.restaurant_response ? 1 : 0) - (b.restaurant_response ? 1 : 0);
        case 'with_photos':
          return (b.photos?.length || 0) - (a.photos?.length || 0);
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

  return (
    <DashboardLayout title="Avis clients">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-600 fill-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.avg_rating?.toFixed(1) || '-'}</p>
              <p className="text-sm text-gray-500">Note moyenne</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Avis total</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {reviews.filter(r => r.rating >= 4).length}
              </p>
              <p className="text-sm text-gray-500">Avis positifs</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Camera className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.photos_count || 0}</p>
              <p className="text-sm text-gray-500">Avec photos</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.without_response || 0}</p>
              <p className="text-sm text-gray-500">Sans réponse</p>
            </div>
          </div>
        </div>
      </div>

      {/* Rating Distribution */}
      {stats.distribution && Object.keys(stats.distribution).length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Répartition des notes</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = stats.distribution[rating] || 0;
              const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-12">
                    <span className="text-sm font-medium">{rating}</span>
                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  </div>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400 rounded-full transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={sortFilter} onValueChange={setSortFilter}>
          <SelectTrigger className="w-48 rounded-xl">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récents</SelectItem>
            <SelectItem value="rating_high">Note : haute → basse</SelectItem>
            <SelectItem value="rating_low">Note : basse → haute</SelectItem>
            <SelectItem value="without_response">Sans réponse d'abord</SelectItem>
            <SelectItem value="with_photos">Avec photos d'abord</SelectItem>
          </SelectContent>
        </Select>

        <Select value={ratingFilter} onValueChange={setRatingFilter}>
          <SelectTrigger className="w-40 rounded-xl">
            <Star className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filtrer par note" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les notes</SelectItem>
            <SelectItem value="5">5 étoiles</SelectItem>
            <SelectItem value="4">4 étoiles</SelectItem>
            <SelectItem value="3">3 étoiles</SelectItem>
            <SelectItem value="2">2 étoiles</SelectItem>
            <SelectItem value="1">1 étoile</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Chargement...</div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun avis pour le moment</p>
            <p className="text-sm text-gray-400 mt-1">
              Les avis apparaîtront ici après que vos clients aient terminé leurs commandes
            </p>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="text-emerald-700 font-semibold">
                      {review.user_name?.charAt(0).toUpperCase() || review.client_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">{review.user_name || review.client_name}</p>
                      {review.is_verified_purchase && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Achat vérifié
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(review.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= review.rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-200 text-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <Badge className={
                    review.rating >= 4 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : review.rating >= 3 
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                  }>
                    {review.rating}/5
                  </Badge>
                </div>
              </div>

              {/* Comment */}
              {review.comment && (
                <p className="text-gray-600 mb-4">{review.comment}</p>
              )}

              {/* Photos */}
              {review.photos && review.photos.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {review.photos.map((photo, index) => (
                    <img
                      key={index}
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setSelectedPhoto(photo)}
                    />
                  ))}
                </div>
              )}

              {/* Restaurant Response */}
              {review.restaurant_response || review.response ? (
                <div className="bg-gray-50 rounded-xl p-4 mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-1">Votre réponse:</p>
                  <p className="text-gray-600">{review.restaurant_response || review.response}</p>
                  {review.response_date && (
                    <p className="text-xs text-gray-400 mt-2">
                      Répondu le {new Date(review.response_date).toLocaleDateString('fr-FR')}
                    </p>
                  )}
                </div>
              ) : respondingTo === review.id ? (
                <div className="mt-4 space-y-3">
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Écrivez votre réponse..."
                    rows={3}
                    className="rounded-xl"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleSubmitResponse(review.id)}
                      disabled={submitting}
                      className="bg-emerald-500 hover:bg-emerald-600 rounded-full"
                    >
                      {submitting ? 'Envoi...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setRespondingTo(null);
                        setResponseText('');
                      }}
                      className="rounded-full"
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRespondingTo(review.id)}
                  className="mt-3 rounded-full"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Répondre
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Photo Modal */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden">
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-2 right-2 z-10 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedPhoto && (
            <img src={selectedPhoto} alt="Photo avis" className="w-full h-auto" />
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
